import { RouteConfig, Route } from 'vue-router';
import { ResponsibleGamingPage as RGPage } from '@gig/magic-responsible-gaming-types';
import { ipCountryQuery, isDesktopQuery, ModalSize } from '@gig/magic-common';

import {
    AccountPageNames,
    getAccountComponent,
    getONJNComponent,
    getRGPComponent,
    getScanVerificationComponent,
    getScanVerificationTitle,
    SIGN_UP_FORM_STEPS_QUERY,
} from './account.constants';
import {
    authenticationGuard,
    PassThrough,
    AuthRestriction,
    lightTopNavigation,
    modalGuard,
    allowWithPendingConsents,
    allowPendingVerification,
    featureGuard,
    topNavigation,
    config,
    allowWithPendingGracePeriod,
    regilyGuard,
} from '../../services';
import type { IModalRoute } from '../../services';
import { RouteName } from '../routes.constants';
import {
    Feature,
    getPasswordResetFeatures,
    PasswordResetTypes,
    objectValueOrDefault,
} from '../../util';
import { Jurisdiction } from '@gig/magic-jurisdictions/dist/jurisdictions.constants';
import { getUserJurisdiction } from '../../services/routing/helpers/jurisdiction.helpers';
import { allowedCountriesGuard } from '../../services/routing/guards/allowed-countries.guard';

const getSignupStepsTitle = (prefix: string) => (route: Route) =>
    `${prefix}${route.params.step || 1}`;
const getSignUpTitle = getSignupStepsTitle('sign-up.title.step-');
const getBankidSignUpTitle = getSignupStepsTitle('sign-up-bankid.title.step-');

const signUpModalOptions = {
    options: (route: IModalRoute) => {
        if (route.params.step === '3') {
            return {
                dismissibleBackdrop: false,
                dismissible: false,
            };
        }

        return {};
    },
};

export const AccountRoutes: RouteConfig[] = [
    {
        name: RouteName.ACCOUNT,
        path: '',
        beforeEnter: async (to, _from, next) => {
            if (to.name === RouteName.ACCOUNT && (await isDesktopQuery())) {
                const maybeLang = to.params.language
                    ? `/${to.params.language}/`
                    : '';
                next({
                    path: `${maybeLang}${RouteName.ACCOUNT}/${RouteName.PROFILE}`,
                });
            } else {
                next();
            }
        },
        component: getAccountComponent('AccountPage'),
        meta: {
            timedOutAllowed: true,
            ...lightTopNavigation('account.title'),
            ...authenticationGuard(AuthRestriction.AUTH_REQUIRED),
            ...allowPendingVerification(),
        },
        children: [
            {
                name: RouteName.PROFILE,
                path: 'profile',
                component: getAccountComponent('ProfilePage'),
                meta: {
                    ...lightTopNavigation('profile.title'),
                    page: AccountPageNames.PROFILE,
                    timedOutAllowed: true,
                },
            },
            {
                name: RouteName.ACCOUNT_LINKING,
                path: 'account-linking',
                component: getAccountComponent('AccountLinkingPage'),
                meta: {
                    ...modalGuard(
                        getAccountComponent('AccountLinkingPage'),
                        'account-linking.title',
                    ),
                    ...authenticationGuard(AuthRestriction.AUTH_REQUIRED),
                    ...lightTopNavigation('account-linking.title', true),
                },
            },
            {
                name: RouteName.CHANGE_EMAIL,
                path: 'change-email',
                component: getAccountComponent('ChangeEmailPage'),
                meta: { page: AccountPageNames.PROFILE },
            },
            {
                name: RouteName.CHANGE_PASSWORD,
                path: 'change-password',
                component: getAccountComponent('ChangePasswordPage'),
                meta: {
                    page: AccountPageNames.PROFILE,
                    timedOutAllowed: true,
                },
            },
            {
                name: RouteName.CHANGE_SECURITY_QUESTION,
                path: 'change-security-question',
                component: getAccountComponent('ChangeSecurityQuestionPage'),
                meta: {
                    page: AccountPageNames.PROFILE,
                },
            },
            {
                name: RouteName.MARKETING_PREFERENCES,
                path: 'marketing-preferences',
                component: getAccountComponent('MarketingPreferencesPage'),
                meta: {
                    ...featureGuard(Feature.MARKETING_PREFERENCES),
                    ...lightTopNavigation('marketing-preferences.title'),
                    page: AccountPageNames.MARKETING_PREFS,
                },
            },
            {
                name: RouteName.VERIFICATION,
                path: 'verification',
                component: getRGPComponent('VerificationPage'),
                meta: {
                    ...featureGuard(Feature.VERIFICATION),
                    ...lightTopNavigation('verification.title'),
                    page: AccountPageNames.VERIFICATION,
                },
            },
            {
                /* This is just a placeholder, customize this section to meet customer's needs */
                name: RouteName.ACCOUNT_CUSTOM_ITEM,
                path: 'custom-item',
                component: undefined,
            },
            {
                name: RouteName.ACCOUNT_CLOSURE,
                path: 'closure',
                component: PassThrough,
                meta: {
                    shouldDisableRGAlerts: true,
                    ...allowWithPendingConsents(true),
                    ...lightTopNavigation('account-closure.title'),
                    page: AccountPageNames.CLOSURE,
                },
                children: [
                    {
                        name: RouteName.ACCOUNT_CLOSURE_PROMPT,
                        path: '',
                        component: getAccountComponent('ClosurePromptPage'),
                    },
                    {
                        name: RouteName.ACCOUNT_CLOSURE_FORM,
                        path: 'form',
                        component: getAccountComponent('ClosureFormPage'),
                    },
                    {
                        name: RouteName.ACCOUNT_CLOSURE_ERROR,
                        path: 'error/:reason?',
                        component: getAccountComponent('ClosureErrorPage'),
                    },
                    {
                        name: RouteName.ACCOUNT_CLOSURE_SUCCESS,
                        path: 'success',
                        component: getAccountComponent('ClosureSuccessPage'),
                        meta: {
                            ...authenticationGuard(
                                AuthRestriction.UNRESTRICTED,
                            ),
                        },
                    },
                ],
            },
            {
                name: RouteName.RESPONSIBLE_GAMING,
                path: 'responsible-gaming',
                component: getRGPComponent('ResponsibleGamingPage'),
                meta: {
                    ...featureGuard(Feature.RESPONSIBLE_GAMING_FEATURES),
                    ...lightTopNavigation('responsible-gaming.title'),
                    page: AccountPageNames.RESPONSIBLE_GAMING,
                    ...allowWithPendingConsents(true),
                    shouldDisableRGAlerts: true,
                    timedOutAllowed: true,
                },
                beforeEnter: (to, from, next) => {
                    if (from.name === RouteName.RESPONSIBLE_GAMING_PAGE) {
                        return next({
                            name: RouteName.VIEW_LIMITS
                        });
                    }
                    next();
                },
                children: [
                    {
                        name: RouteName.VIEW_LIMITS,
                        path: 'view-limits',
                        component: getRGPComponent('ViewLimitsPage'),
                        meta: {
                            responsibleGamingPage: RGPage.LIMITS,
                            timedOutAllowed: true,
                        },
                    },
                    {
                        name: RouteName.REALITY_CHECK,
                        path: 'reality-check',
                        component: getRGPComponent('RealityCheckPage'),
                        meta: {
                            responsibleGamingPage: RGPage.REALITY_CHECK,
                            timedOutAllowed: true,
                        },
                    },
                    {
                        name: RouteName.CREATE_LIMITS,
                        path: 'create-limits',
                        component: getRGPComponent('CreateLimitsPage'),
                        meta: {
                            responsibleGamingPage: RGPage.LIMITS,
                            timedOutAllowed: true,
                        },
                    },
                    {
                        name: RouteName.SELF_EXCLUDE,
                        path: 'self-exclude',
                        component: getRGPComponent('SelfExcludePage'),
                        meta: {
                            responsibleGamingPage: RGPage.SELF_EXCLUDE,
                            timedOutAllowed: true,
                        },
                    },
                    {
                        name: RouteName.TIME_OUT,
                        path: 'time-out',
                        component: getRGPComponent('TimeOutPage'),
                        meta: {
                            responsibleGamingPage: RGPage.TIME_OUT,
                            timedOutAllowed: true,
                        },
                    },
                    {
                        name: RouteName.PLAY_CONTROL,
                        path: 'play-control',
                        component: getRGPComponent('PlayControlPage'),
                        meta: {
                            responsibleGamingPage: RGPage.PLAY_CONTROL,
                            timedOutAllowed: true,
                        },
                    },
                ],
            },
            {
                name: RouteName.PANIC_SELF_EXCLUDE,
                path: 'panic-self-exclude',
                component: getRGPComponent('PanicSelfExcludePage'),
                meta: {
                    ...lightTopNavigation('panic-self-exclude.title'),
                    ...modalGuard(
                        getRGPComponent('PanicSelfExcludePage'),
                        null,
                    ),
                    ...allowWithPendingConsents(true),
                    shouldDisableRGAlerts: true,
                },
            },
            {
                name: RouteName.SOW,
                path: 'sow',
                component: getRGPComponent('SowPage'),
                meta: {
                    ...featureGuard(Feature.SOURCE_OF_WEALTH),
                    ...lightTopNavigation('sow.title'),
                    ...modalGuard(getRGPComponent('SowPage'), 'sow.title'),
                    page: AccountPageNames.SOURCE_OF_WEALTH,
                },
            },
            {
                name: RouteName.USER_INFO,
                path: 'user-info',
                component: getRGPComponent('UserInfoPage'),
                meta: {
                    ...modalGuard(
                        getRGPComponent('UserInfoPage'),
                        'user-info.heading',
                        {
                            desktopOnly: true,
                            options: {
                                size: ModalSize.LARGE,
                            },
                        },
                    ),
                },
            },
            {
                name: RouteName.SET_DEPOSIT_LIMITS,
                path: 'set-deposit-limits',
                component: getRGPComponent('SetDepositLimitsPage'),
                meta: {
                    ...modalGuard(
                        getRGPComponent('SetDepositLimitsPage'),
                        'set-deposit-limits.title',
                        {
                            desktopOnly: true,
                            options: {
                                size: ModalSize.DEFAULT,
                            },
                        },
                    ),
                },
            },
            {
                name: RouteName.SET_LIMITS,
                path: 'set-limits',
                component: getRGPComponent('SetLimitsPage'),
                meta: {
                    ...modalGuard(getRGPComponent('SetLimitsPage'), null, {
                        desktopOnly: true,
                        options: {
                            size: ModalSize.DEFAULT,
                            dismissible: false,
                            backArrow: false,
                        },
                    }),
                },
            },
            {
                name: RouteName.SET_BETTING_LIMITS,
                path: 'set-betting-limits',
                component: getRGPComponent('SetBettingLimitsPage'),
                meta: {
                    ...topNavigation({
                        title: 'set-betting-limits.title',
                        showBack: false,
                    }),
                    ...modalGuard(
                        getRGPComponent('SetBettingLimitsPage'),
                        'set-betting-limits.title',
                        {
                            desktopOnly: true,
                            options: {
                                size: ModalSize.DEFAULT,
                                dismissible: false,
                                backArrow: false,
                            },
                        },
                    ),
                },
            },
            {
                name: RouteName.USER_VERIFICATION,
                path: 'unverified-user',
                component: getAccountComponent('VerifyPage'),
                meta: {
                    ...lightTopNavigation('unverified-user.title'),
                    ...allowWithPendingConsents(true),
                    ...allowPendingVerification(),
                    ...modalGuard(
                        getAccountComponent('VerifyPage'),
                        'unverified-user.title',
                        {
                            options: async () => {
                                const jurisdiction =
                                    await getUserJurisdiction();
                                if (jurisdiction === Jurisdiction.ONJN) {
                                    return {
                                        dismissible: true,
                                    };
                                }
                                return {
                                    dismissible: false,
                                };
                            },
                        },
                    ),
                },
            },
            {
                name: RouteName.PENDING_VERIFICATION,
                path: 'pending-verification',
                component: getONJNComponent('ONJNPendingVerificationPage'),
                meta: {
                    ...lightTopNavigation('pending-verification.title'),
                    ...allowWithPendingConsents(true),
                    ...allowPendingVerification(),
                    ...modalGuard(
                        getONJNComponent('ONJNPendingVerificationPage'),
                        'pending-verification.title',
                    ),
                },
            },
            {
                name: RouteName.IDENTITY_VERIFICATION,
                path: 'identity-verification',
                component: getScanVerificationComponent(),
                meta: {
                    ...modalGuard(
                        getScanVerificationComponent(),
                        getScanVerificationTitle,
                        {
                            options: {
                                size: ModalSize.DEFAULT,
                                dismissible: false,
                                backArrow: false,
                            },
                        },
                    ),
                    ...topNavigation({
                        title: getScanVerificationTitle,
                        showBack: false,
                    }),
                    ...featureGuard(Feature.SCAN_VERIFY_FLOW),
                },
            },
            {
                name: RouteName.IDENTITY_VERIFICATION_COMPLETE,
                path: 'identity-verification-complete',
                component: getScanVerificationComponent(),
                props: {
                    complete: true,
                },
                meta: {
                    ...modalGuard(
                        getScanVerificationComponent(),
                        getScanVerificationTitle,
                        {
                            options: {
                                size: ModalSize.DEFAULT,
                                dismissible: false,
                                backArrow: false,
                            },
                            propData: {
                                complete: true,
                            },
                        },
                    ),
                    ...topNavigation({
                        title: getScanVerificationTitle,
                        showBack: false,
                    }),
                    ...featureGuard(Feature.SCAN_VERIFY_FLOW),
                },
            },
        ],
    },
    {
        name: RouteName.LOGIN,
        path: 'login',
        component: getAccountComponent('LoginPage'),
        meta: {
            ...modalGuard(getAccountComponent('LoginPage'), 'login.title'),
            ...authenticationGuard(AuthRestriction.AUTH_FORBIDDEN),
            ...lightTopNavigation('login.title', true),
            ...allowedCountriesGuard(),
        },
    },
    {
        name: RouteName.LOGIN_PNP,
        path: 'login-pnp',
        component: getAccountComponent('LoginPage'),
        meta: {
            ...modalGuard(getAccountComponent('LoginPage'), 'login.title'),
            ...authenticationGuard(AuthRestriction.AUTH_FORBIDDEN),
            ...lightTopNavigation('login.title', true),
            ...allowedCountriesGuard(),
        },
    },
    {
        name: RouteName.SIGN_UP,
        path: 'sign-up/:step?/:token?',
        beforeEnter: regilyGuard,
        component: getAccountComponent('SignUpPageV2'),
        meta: {
            ...modalGuard(getAccountComponent('SignUpPageV2'), getSignUpTitle, {
                options: async (route, router) => {
                    const currentIpCountry = await ipCountryQuery();
                    const preferredVerificationMethods = objectValueOrDefault(
                        config.signup.preferredVerificationMethod,
                        currentIpCountry,
                    );
                    let backArrow: boolean | string = false;
                    const { step } = route.params;
                    if (step) {
                        const formSteps: number = await window.queryBus.query(
                            SIGN_UP_FORM_STEPS_QUERY,
                        );

                        if (step === '1') {
                            backArrow = false;
                        } else if (+step <= formSteps) {
                            const previousStep = +step - 1;
                            backArrow = router.resolve({
                                name: RouteName.SIGN_UP,
                                params: {
                                    step: previousStep.toString(),
                                },
                            }).href;
                        } else if (
                            !preferredVerificationMethods.includes('EMAIL')
                        ) {
                            backArrow = true;
                        }
                    }
                    return {
                        dismissibleBackdrop: false,
                        backArrow,
                    };
                },
            }),
            ...authenticationGuard(AuthRestriction.AUTH_FORBIDDEN),
            ...lightTopNavigation(getSignUpTitle, true),
            ...allowedCountriesGuard(),
            alwaysScrollToTop: true,
        },
    },
    {
        name: RouteName.SIGN_UP_BANKID,
        path: 'sign-up-bankid/:step?',
        component: getAccountComponent('SignUpBankidPage'),
        meta: {
            ...modalGuard(
                getAccountComponent('SignUpBankidPage'),
                getBankidSignUpTitle,
                signUpModalOptions,
            ),
            ...lightTopNavigation(getBankidSignUpTitle, true),
            ...authenticationGuard(AuthRestriction.UNRESTRICTED),
            ...allowedCountriesGuard(),
            alwaysScrollToTop: true,
        },
    },
    {
        name: RouteName.SIGN_UP_HYBRID,
        path: 'sign-up-pnp/:step?',
        component: getAccountComponent('SignUpBankidPage'),
        meta: {
            ...modalGuard(
                getAccountComponent('SignUpBankidPage'),
                'sign-up-pnp.title',
                signUpModalOptions,
            ),
            ...lightTopNavigation('sign-up-pnp.title', true),
            ...authenticationGuard(AuthRestriction.UNRESTRICTED),
            ...allowedCountriesGuard(),
            alwaysScrollToTop: true,
        },
    },
    {
        name: RouteName.BANK_ID_REDIRECT,
        path: 'bank-id-redirect',
        component: getAccountComponent('BankIdRedirect'),
    },
    {
        path: RouteName.PASSWORD_RESET,
        component: PassThrough,
        children: [
            {
                name: RouteName.PASSWORD_RESET,
                path: '',
                async beforeEnter(_, __, next) {
                    const [primaryType] = await getPasswordResetFeatures();

                    switch (primaryType) {
                        case PasswordResetTypes.SMS:
                            next({ name: RouteName.PASSWORD_RESET_SMS });
                            break;
                        case PasswordResetTypes.EMAIL:
                        default:
                            next({ name: RouteName.PASSWORD_RESET_EMAIL });
                            break;
                    }
                },
            },
            {
                name: RouteName.PASSWORD_RESET_EMAIL,
                path: 'email/',
                component: getAccountComponent('PasswordResetRequestPage'),
                meta: {
                    ...featureGuard(Feature.PASSWORD_RESET_EMAIL),
                    ...modalGuard(
                        getAccountComponent('PasswordResetRequestPage'),
                        'password-reset.title',
                    ),
                },
            },
            {
                name: RouteName.PASSWORD_RESET_FORM,
                path: 'email/:token',
                component: getAccountComponent('PasswordResetFormPage'),
                meta: {
                    ...featureGuard(Feature.PASSWORD_RESET_EMAIL),
                    ...modalGuard(
                        getAccountComponent('PasswordResetFormPage'),
                        'password-reset.title',
                    ),
                },
            },
            {
                name: RouteName.PASSWORD_RESET_SMS,
                path: 'sms/',
                component: getAccountComponent('PasswordResetSMSPage'),
                meta: {
                    ...lightTopNavigation('password-reset.sms.title'),
                    ...featureGuard(Feature.PASSWORD_RESET_SMS),
                    ...modalGuard(
                        getAccountComponent('PasswordResetSMSPage'),
                        'password-reset.sms.title',
                        {
                            options: {
                                backArrow: false,
                            },
                        },
                    ),
                },
            },
            {
                name: RouteName.PASSWORD_RESET_QUESTION,
                path: 'question',
                component: getAccountComponent('PasswordResetQuestionPage'),
                meta: {
                    ...modalGuard(
                        getAccountComponent('PasswordResetQuestionPage'),
                        'password-reset.title',
                    ),
                },
            },
            {
                name: RouteName.PASSWORD_RESET_CONFIRMATION,
                path: 'confirmation',
                component: getAccountComponent('PasswordResetConfirmationPage'),
                meta: {
                    ...modalGuard(
                        getAccountComponent('PasswordResetConfirmationPage'),
                        'password-reset.title',
                    ),
                },
            },
            {
                name: RouteName.PASSWORD_RESET_SUCCESS,
                path: 'complete',
                component: getAccountComponent('PasswordResetSuccessPage'),
                meta: {
                    ...modalGuard(
                        getAccountComponent('PasswordResetSuccessPage'),
                        'password-reset.title',
                    ),
                },
            },
            // Backwards compatibility with old password reset routing before SMS came along.
            {
                path: ':token',
                redirect: (to) => ({
                    name: RouteName.PASSWORD_RESET_FORM,
                    params: to.params,
                }),
            },
        ],
        meta: {
            ...authenticationGuard(AuthRestriction.AUTH_FORBIDDEN),
            ...lightTopNavigation('password-reset.title', true),
        },
    },
    // No auth check as we shouldn't redirect to login for logging out.
    {
        name: RouteName.LOGOUT,
        path: 'logout',
        component: getAccountComponent('LogoutPage'),
        meta: {
            ...allowWithPendingGracePeriod(),
            ...allowWithPendingConsents(true),
            timedOutAllowed: true,
            shouldDisableRGAlerts: true,
            allowWithoutBettingLimits: true,
            ...allowPendingVerification(),
        },
    },
    {
        name: RouteName.IMPERSONATE,
        path: 'impersonate/:token',
        component: getAccountComponent('ImpersonatePage'),
    },
];
